/**
 * Основные параметры задачи.
 *
 * §2.3: поля «Отдел» здесь НЕТ и быть не может — departmentId вычисляется
 * из доски и не принимается от клиента. §11.5: исполнителей может быть
 * несколько, набор передаётся целиком.
 *
 * Наблюдатели получают уведомления по задаче (комментарий, статус, столбец,
 * исполнители, срок, тип, приоритет), не отвечая за неё. «Следить» — себя,
 * любому участнику доски; других — тому, кто назначает исполнителей.
 */

import { Permission, ProjectBindingMode, StatusSource, TaskSection } from "@taskflow/contracts";
import { AlarmClock, Eye, EyeOff } from "lucide-react";
import { useState } from "react";

import { useAppSelector } from "@/app/hooks";
import { usePermission, type WsError } from "@taskflow/client-core";
import { formatDate, todayIso } from "@/shared/lib/date";
import {
  Avatar,
  Button,
  DateInput,
  fullNameOf,
  MultiSelectPicker,
  type PickerOption,
  PriorityBadge,
  Select,
  StatusTag,
  Tag,
  toast,
} from "@/shared/ui";

import {
  type DictionaryItem,
  usePrioritiesQuery,
  useTaskTypesQuery,
} from "@taskflow/client-core/admin";
import {
  type BoardDetail,
  useBoardMembersQuery,
  useBoardTagsQuery,
} from "@taskflow/client-core/boards";
import { STATUS_CATEGORIES } from "../../boards/statusCategories";
import {
  type TaskDetail,
  useMoveTaskToColumnMutation,
  useSetAssigneesMutation,
  useSetStatusTagMutation,
  useSetTaskTagsMutation,
  useSetWatchersMutation,
  useUpdateTaskMutation,
} from "@taskflow/client-core/tasks";
import { systemStatusOptions } from "@taskflow/client-core/boards";
import { reminderLabel } from "@taskflow/client-core/notes";
import { Section } from "./Section";
import { TaskReminderDialog } from "./TaskReminderDialog";

export function TaskParameters({
  task,
  boardId,
  board,
  statusSource,
  canEdit,
  boardPermissions,
}: {
  task: TaskDetail;
  boardId: string;
  /** undefined — доска ещё загружается. */
  board?: BoardDetail | undefined;
  /** null — доска ещё загружается. */
  statusSource: StatusSource | null;
  canEdit: boolean;
  boardPermissions: readonly Permission[];
}) {
  const members = useBoardMembersQuery(boardId, { skip: boardId === "" });
  const tags = useBoardTagsQuery(boardId, { skip: boardId === "" });
  const [setAssignees, assigneesState] = useSetAssigneesMutation();
  const [setWatchers, watchersState] = useSetWatchersMutation();
  const [setTags] = useSetTaskTagsMutation();
  const [setStatusTag, statusSaving] = useSetStatusTagMutation();
  const [updateTask, updateState] = useUpdateTaskMutation();
  // Архивные значения нужны, чтобы показать уже стоящее у задачи;
  // новым выбором Select их не предлагает (§7.1).
  const priorities = usePrioritiesQuery({ includeArchived: true }, { skip: !canEdit });
  const taskTypes = useTaskTypesQuery({ includeArchived: true }, { skip: !canEdit });

  const canAssign = usePermission(Permission.TASK_ASSIGN, boardPermissions);
  const canMove = usePermission(
    [Permission.TASK_MOVE_OWN, Permission.TASK_MOVE_ALL],
    boardPermissions,
  );
  const [moveToColumn, moveState] = useMoveTaskToColumnMutation();
  const canTag = usePermission(Permission.TAG_ASSIGN, boardPermissions);

  const personal = task.boardIsPersonal === true || board?.isPersonal === true;
  const [assigneeError, setAssigneeError] = useState<string | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const currentUserId = useAppSelector((state) => state.auth.user?.id);

  const memberOptions: PickerOption[] = (members.data?.data ?? []).map((member) => ({
    id: member.userId,
    label: fullNameOf(member),
    ...(member.position === null ? {} : { hint: member.position }),
  }));

  // Системные статусные теги в этот список НЕ попадают: §10.2 запрещает
  // назначать статус как обычный тег.
  const tagOptions: PickerOption[] = (tags.data?.data ?? [])
    .filter((tag) => tag.kind === "REGULAR")
    .map((tag) => ({
      id: tag.id,
      label: tag.name,
      archived: tag.status === "ARCHIVED",
    }));

  // Кнопка «Я» — только участнику доски: исполнителем может быть лишь он.
  const canAssignSelf =
    currentUserId !== undefined && memberOptions.some((option) => option.id === currentUserId);
  const selfAssigned = task.assignees.some((person) => person.id === currentUserId);

  // Наблюдать можно и закрытую на правку задачу (роль «наблюдатель» доски),
  // но не архивную: событий по ней больше не будет.
  const watcherIds = task.watchers.map((person) => person.id);
  const watching = currentUserId !== undefined && watcherIds.includes(currentUserId);
  const canWatchSelf = canAssignSelf && !task.archived;
  const canSetWatchers = canAssign && canEdit;

  const applyWatchers = async (userIds: string[]) => {
    try {
      await setWatchers({ key: task.key, userIds }).unwrap();
    } catch (raw) {
      toast.error(raw as WsError);
    }
  };

  const applyAssignees = async (userIds: string[]) => {
    setAssigneeError(null);
    try {
      await setAssignees({ key: task.key, version: task.version, userIds }).unwrap();
    } catch (raw) {
      const error = raw as WsError;
      if (error.status === 409) {
        setAssigneeError("Задачу изменили параллельно — обновите панель");
      } else {
        toast.error(error);
      }
    }
  };

  // Ручной выбор статуса — только на доске «проект на столбце»: там статус
  // несёт системный тег. На доске «проект на доске» статус задаёт столбец.
  const statusByTag = statusSource === StatusSource.SYSTEM_TAG;
  const statusOptions = systemStatusOptions(tags.data?.data);
  const currentStatusId =
    (tags.data?.data ?? []).find(
      (tag) => tag.kind === "SYSTEM_STATUS" && tag.systemCode === task.statusTag?.code,
    )?.id ?? null;

  const applyStatus = async (tagId: string) => {
    if (tagId === currentStatusId) return;
    try {
      await setStatusTag({ key: task.key, version: task.version, tagId }).unwrap();
    } catch (raw) {
      const error = raw as WsError;
      if (error.status === 409) {
        toast.error("Задачу изменили параллельно — обновите панель");
      } else {
        toast.error(error);
      }
    }
  };

  // На доске «один проект на всю доску» задачу видно и вне доски — из
  // отчётов и списка задач отдела. Там показывается, на какой она доске,
  // а столбец меняется прямо из карточки, как перетаскиванием на доске.
  const singleProjectBoard = board?.projectBindingMode === ProjectBindingMode.BOARD;
  const currentColumn = board?.columns.find((column) => column.id === task.columnId);
  // §17.4: на доске «проект на доске» статус задачи — статус её столбца,
  // а не название: «Готово (предзаливка)» имеет статус «Выполнено».
  const columnStatusLabel = STATUS_CATEGORIES.find(
    (category) => category.id === currentColumn?.statusCategory,
  )?.label;
  // Те же правила переходов, что при перетаскивании (§12.3): пустой список
  // переходов — можно в любой столбец.
  const columnOptions: PickerOption[] = (board?.columns ?? [])
    .filter(
      (column) =>
        column.isActive &&
        (column.id === task.columnId ||
          currentColumn === undefined ||
          currentColumn.allowedTargets.length === 0 ||
          currentColumn.allowedTargets.includes(column.id)),
    )
    .map((column) => ({ id: column.id, label: column.name }));

  const applyColumn = async (columnId: string) => {
    if (columnId === task.columnId) return;
    try {
      await moveToColumn({ key: task.key, toColumnId: columnId, version: task.version }).unwrap();
    } catch (raw) {
      const error = raw as WsError;
      if (error.status === 409) {
        toast.warning("Задача изменилась", "Кто-то отредактировал её параллельно. Обновите панель.");
      } else {
        toast.error(error, "Не удалось перенести задачу");
      }
    }
  };

  const dictionaryOptions = (items: DictionaryItem[] | undefined): PickerOption[] =>
    [...(items ?? [])]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((item) => ({ id: item.id, label: item.name, archived: item.status === "ARCHIVED" }));
  const priorityOptions = dictionaryOptions(priorities.data?.data);
  const typeOptions = dictionaryOptions(taskTypes.data?.data);

  const applyUpdate = async (
    fields: {
      priorityId?: string;
      typeId?: string;
      dueDate?: string | null;
      reporterId?: string;
    },
  ) => {
    try {
      await updateTask({ key: task.key, version: task.version, ...fields }).unwrap();
    } catch (raw) {
      const error = raw as WsError;
      if (error.status === 409) {
        toast.warning("Задача изменилась", "Кто-то отредактировал её параллельно. Обновите панель.");
      } else {
        toast.error(error);
      }
    }
  };

  const applyTags = async (tagIds: string[]) => {
    try {
      await setTags({ key: task.key, version: task.version, tagIds }).unwrap();
    } catch (raw) {
      toast.error(raw as WsError);
    }
  };

  return (
    <Section title="Основные параметры" section={TaskSection.PARAMETERS}>
      <dl className="grid grid-cols-[minmax(120px,auto)_1fr] gap-x-4 gap-y-3 text-[13px]">
        <Field label="Статус">
          {statusByTag && canEdit && statusOptions.length > 0 ? (
            <div className="max-w-sm">
              <Select
                label="Статус задачи"
                placeholder="Выберите статус"
                options={statusOptions}
                value={currentStatusId}
                onChange={(tagId) => void applyStatus(tagId)}
                disabled={statusSaving.isLoading}
              />
            </div>
          ) : task.statusTag === null ? (
            <span className="text-fg">{columnStatusLabel ?? task.columnName}</span>
          ) : (
            // §2.4: у ОАП статус — системный тег, а не название столбца.
            <StatusTag label={task.statusTag.label} code={task.statusTag.code} />
          )}
        </Field>

        {singleProjectBoard && (
          <Field label="Доска">
            <span className="text-fg">{task.boardName}</span>
          </Field>
        )}

        <Field label="Столбец">
          {/* Переносит право на перемещение, а не на правку — как на доске. */}
          {singleProjectBoard && !task.archived && canMove && columnOptions.length > 1 ? (
            <div className="max-w-sm">
              <Select
                label="Столбец задачи"
                placeholder="Выберите столбец"
                options={columnOptions}
                value={task.columnId}
                disabled={moveState.isLoading}
                onChange={(columnId) => void applyColumn(columnId)}
              />
            </div>
          ) : (
            <span className="text-fg">{task.columnName}</span>
          )}
        </Field>

        {/* Личная доска: у задачи один человек — проект, постановщик,
            исполнители, наблюдатели и автор в карточке не нужны. */}
        {!personal && (
          <>
            <Field label="Проект">
              <span className="text-fg">{task.projectName ?? "—"}</span>
            </Field>

            {/* Постановщик — кто поставил задачу; может отличаться от автора
                карточки. Меняет его любой участник доски с правом правки,
                а не только автор: постановку часто уточняют уже по ходу дела.
                Список — участники доски: чужой постановщик не увидит задачу. */}
            <Field label="Постановщик">
              {canEdit ? (
                <div className="max-w-sm">
                  <Select
                    label="Постановщик задачи"
                    placeholder={members.isLoading ? "Загрузка…" : "Не указан"}
                    options={memberOptions}
                    value={task.reporterId}
                    disabled={updateState.isLoading}
                    onChange={(id) => {
                      if (id !== task.reporterId) void applyUpdate({ reporterId: id });
                    }}
                  />
                </div>
              ) : task.reporterName === null ? (
                <span className="text-faint">Не указан</span>
              ) : (
                <span className="text-fg">{task.reporterName}</span>
              )}
            </Field>

            <Field label="Исполнители">
              {canAssign && canEdit ? (
                <div className="max-w-sm">
                  <div className="flex gap-2">
                    <div className="min-w-0 flex-1">
                      <MultiSelectPicker
                        label="Исполнители задачи"
                        placeholder="Не назначены"
                        options={memberOptions}
                        value={task.assignees.map((a) => a.id)}
                        onChange={(ids) => void applyAssignees(ids)}
                      />
                    </div>
                    {canAssignSelf && currentUserId !== undefined && (
                      <Button
                        variant="secondary"
                        className="shrink-0 cursor-pointer"
                        aria-label="Назначить меня исполнителем"
                        title={selfAssigned ? "Вы уже исполнитель" : "Назначить меня исполнителем"}
                        disabled={selfAssigned}
                        loading={assigneesState.isLoading}
                        onClick={() =>
                          void applyAssignees([...task.assignees.map((a) => a.id), currentUserId])
                        }
                      >
                        Я
                      </Button>
                    )}
                  </div>
                  {assigneeError !== null && (
                    <p role="alert" className="mt-1 text-[11px] font-semibold text-danger">
                      {assigneeError}
                    </p>
                  )}
                </div>
              ) : task.assignees.length === 0 ? (
                <span className="text-faint">Не назначены</span>
              ) : (
                <ul className="flex flex-col gap-1">
                  {task.assignees.map((person) => (
                    <li key={person.id} className="flex items-center gap-1.5">
                      <Avatar person={person} size="xs" />
                      <span className="text-fg">{fullNameOf(person)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Field>

            <Field label="Наблюдатели">
              <div className="flex max-w-sm flex-col gap-1">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    {canSetWatchers ? (
                      <MultiSelectPicker
                        label="Наблюдатели задачи"
                        placeholder="Нет наблюдателей"
                        options={memberOptions}
                        value={watcherIds}
                        onChange={(ids) => void applyWatchers(ids)}
                      />
                    ) : task.watchers.length === 0 ? (
                      <span className="text-faint">Нет наблюдателей</span>
                    ) : (
                      <ul className="flex flex-col gap-1">
                        {task.watchers.map((person) => (
                          <li key={person.id} className="flex items-center gap-1.5">
                            <Avatar person={person} size="xs" />
                            <span className="text-fg">{fullNameOf(person)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {canWatchSelf && currentUserId !== undefined && (
                    <Button
                      variant="secondary"
                      size={canSetWatchers ? "md" : "sm"}
                      icon={watching ? EyeOff : Eye}
                      className="shrink-0 cursor-pointer"
                      loading={watchersState.isLoading}
                      title={
                        watching
                          ? "Перестать получать уведомления по задаче"
                          : "Получать уведомления по задаче"
                      }
                      onClick={() =>
                        void applyWatchers(
                          watching
                            ? watcherIds.filter((id) => id !== currentUserId)
                            : [...watcherIds, currentUserId],
                        )
                      }
                    >
                      {watching ? "Не следить" : "Следить"}
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-faint">
                  Уведомления о комментариях, статусе и столбце, исполнителях, сроке, типе и приоритете
                </p>
              </div>
            </Field>
          </>
        )}

        <Field label="Приоритет">
          {canEdit ? (
            <div className="max-w-sm">
              <Select
                label="Приоритет задачи"
                placeholder={priorities.isLoading ? "Загрузка…" : "Выберите приоритет"}
                options={priorityOptions}
                value={task.priorityId}
                disabled={updateState.isLoading}
                onChange={(id) => {
                  if (id !== task.priorityId) void applyUpdate({ priorityId: id });
                }}
              />
            </div>
          ) : task.priorityName === null ? (
            <span className="text-faint">—</span>
          ) : (
            <PriorityBadge name={task.priorityName} color={task.priorityColor} />
          )}
        </Field>

        <Field label="Тип">
          {canEdit ? (
            <div className="max-w-sm">
              <Select
                label="Тип задачи"
                placeholder={taskTypes.isLoading ? "Загрузка…" : "Выберите тип"}
                options={typeOptions}
                value={task.typeId}
                disabled={updateState.isLoading}
                onChange={(id) => {
                  if (id !== task.typeId) void applyUpdate({ typeId: id });
                }}
              />
            </div>
          ) : (
            <span className="text-fg">{task.typeName ?? "—"}</span>
          )}
        </Field>

        <Field label="Срок">
          {canEdit ? (
            <DueDateField
              // Новая версия задачи — новое исходное значение поля.
              key={`${task.key}-${String(task.version)}`}
              value={task.dueDate}
              overdue={task.overdue}
              saving={updateState.isLoading}
              onSave={(dueDate) => void applyUpdate({ dueDate })}
            />
          ) : task.dueDate === null ? (
            <span className="text-faint">Не задан</span>
          ) : (
            <span className={task.overdue ? "font-semibold text-danger" : "text-fg"}>
              {formatDate(task.dueDate)}
              {task.overdue && " · срок истёк"}
            </span>
          )}
        </Field>

        <Field label="Напоминание">
          <ReminderValue
            remindAt={task.remindAt ?? null}
            canEdit={canEdit && !task.archived}
            onOpen={() => setReminderOpen(true)}
          />
        </Field>

        <Field label="Теги">
          {canTag && canEdit ? (
            <div className="max-w-sm">
              <MultiSelectPicker
                label="Теги задачи"
                placeholder="Тегов нет"
                options={tagOptions}
                value={task.tags.map((t) => t.id)}
                onChange={(ids) => void applyTags(ids)}
              />
            </div>
          ) : task.tags.length === 0 ? (
            <span className="text-faint">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {task.tags.map((tag) => (
                <Tag key={tag.id} size="sm" color={tag.color}>
                  {tag.name}
                </Tag>
              ))}
            </div>
          )}
        </Field>

        {!personal && (
          <Field label="Автор">
            <span className="text-fg">{task.creatorName}</span>
          </Field>
        )}

        <Field label="Создана">
          <span className="text-muted">{task.createdAtLabel}</span>
        </Field>

        {/* Поля «Отдел» здесь нет намеренно — §2.3. */}
      </dl>
      {reminderOpen && <TaskReminderDialog task={task} onClose={() => setReminderOpen(false)} />}
    </Section>
  );
}

/**
 * Срок сохраняется сразу при выборе дня в календаре: промежуточных дат,
 * как при наборе года вручную в прежнем <input type="date">, больше нет.
 */
function DueDateField({
  value,
  overdue,
  saving,
  onSave,
}: {
  value: string | null;
  overdue: boolean;
  saving: boolean;
  onSave: (dueDate: string | null) => void;
}) {
  const [pastError, setPastError] = useState(false);
  const commit = (next: string) => {
    const normalized = next === "" ? null : next;
    if (normalized === value) return;
    // Уже прошедший срок задачи остаётся, но новый раньше сегодня не ставится.
    if (normalized !== null && normalized < todayIso()) {
      setPastError(true);
      return;
    }
    setPastError(false);
    onSave(normalized);
  };

  return (
    <div className="flex max-w-sm flex-wrap items-center gap-2">
      <div className="w-44">
        {/* Очистка — кнопкой «Убрать срок» рядом, крестик в поле был бы вторым способом. */}
        <DateInput
          min={todayIso()}
          aria-label="Срок задачи"
          value={value ?? ""}
          disabled={saving}
          invalid={overdue}
          clearable={false}
          onChange={commit}
        />
      </div>
      {value !== null && (
        <Button
          size="sm"
          variant="ghost"
          disabled={saving}
          onClick={() => commit("")}
        >
          Убрать срок
        </Button>
      )}
      {overdue && <span className="text-[12px] font-semibold text-danger">срок истёк</span>}
      {pastError && (
        <span role="alert" className="text-[12px] font-semibold text-danger">
          срок не может быть раньше сегодня
        </span>
      )}
    </div>
  );
}

function isPast(iso: string): boolean {
  return new Date(iso).getTime() <= Date.now();
}

/**
 * Напоминание исполнителям. Сработавшее остаётся видно с пометкой
 * «отправлено» — по ней понятно, что уведомление уже ушло, а не потерялось.
 */
function ReminderValue({
  remindAt,
  canEdit,
  onOpen,
}: {
  remindAt: string | null;
  canEdit: boolean;
  onOpen: () => void;
}) {
  const sent = remindAt !== null && isPast(remindAt);
  const label =
    remindAt === null ? null : (
      <span className={sent ? "text-muted" : "text-fg"}>
        {reminderLabel(remindAt)}
        {sent && " · отправлено"}
      </span>
    );
  if (!canEdit) return label ?? <span className="text-faint">Не задано</span>;
  return (
    <div className="flex max-w-sm flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        {label}
        <Button
          size="sm"
          variant={remindAt === null ? "secondary" : "ghost"}
          {...(remindAt === null ? { icon: AlarmClock } : {})}
          className="cursor-pointer"
          onClick={onOpen}
        >
          {remindAt === null ? "Напомнить" : "Изменить"}
        </Button>
      </div>
      {remindAt === null && (
        <p className="text-[11px] text-faint">Исполнители получат уведомление в выбранные дату и время</p>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-faint">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}
